import {
  CsspBrand,
  CsspInStockProduct,
  CsspOfferGroup,
  CsspOffersResult,
  CsspSearchParams,
  CsspSupplierCode,
  CsspSupplierOffer,
  CsspStockWarehouse,
} from "./types";
import { CSSP_GROUP_ORDER } from "./constants";
import { getCsspCity } from "./csspCity";

type RawRecord = Record<string, unknown>;

const asRecord = (value: unknown): RawRecord =>
  value && typeof value === "object" ? (value as RawRecord) : {};

const asArray = (value: unknown): unknown[] => (Array.isArray(value) ? value : []);

const asArrayOrSingleton = (value: unknown): unknown[] => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === "object") return [value];
  return [];
};

const asString = (value: unknown): string => {
  if (typeof value === "string") return value;
  if (typeof value === "number") return String(value);
  return "";
};

const asNumber = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const normalized = Number(value.replace(/\s/g, "").replace(",", "."));
    return Number.isFinite(normalized) ? normalized : null;
  }
  return null;
};

const inStockGroupAliases: Record<CsspOfferGroup, string[]> = {
  originals: ["originals", "original", "search", "searched"],
  replacements: ["replacements", "replacement", "replace", "replaces"],
  analogues: ["analogues", "analogs", "analog", "analogue"],
};

const NON_ALPHANUMERIC_REGEX = /[^A-Z0-9]+/g;
const EDGE_DASHES_REGEX = /^-+|-+$/g;
const MULTIPLE_DASHES_REGEX = /-+/g;

export const normalizeArticleForCompare = (value: string): string =>
  value.toUpperCase().replace(NON_ALPHANUMERIC_REGEX, "");

export const formatArticleForDisplay = (value: string): string =>
  value
    .toUpperCase()
    .trim()
    .replace(NON_ALPHANUMERIC_REGEX, "-")
    .replace(EDGE_DASHES_REGEX, "")
    .replace(MULTIPLE_DASHES_REGEX, "-");

const BRAND_PART_SEPARATOR_REGEX = /\s*\/\s*/g;

export const normalizeBrandForCompare = (value: string): string =>
  value.toLocaleUpperCase("ru-RU").replace(NON_ALPHANUMERIC_REGEX, "");

export const formatBrandForDisplay = (value: string): string => {
  const trimmed = value.trim().replace(BRAND_PART_SEPARATOR_REGEX, " / ");
  if (!trimmed) return "";

  return trimmed
    .split(" / ")
    .map((part) => {
      const normalized = part.trim();
      if (normalized.length <= 3 || normalized === normalized.toUpperCase()) {
        const knownTitleCase: Record<string, string> = {
          HYUNDAI: "Hyundai",
          SSANGYONG: "SsangYong",
        };
        return knownTitleCase[normalized.toUpperCase()] ?? normalized.toUpperCase();
      }

      return normalized;
    })
    .join(" / ");
};

const getDisplayPriority = (value: string): number => {
  let score = 0;
  if (value.includes("-")) score += 2;
  if (value === value.toUpperCase()) score += 1;
  return score;
};

export const choosePreferredArticleDisplay = (
  currentDisplay: string | undefined,
  candidateDisplay: string
): string => {
  if (!currentDisplay) return candidateDisplay;

  const currentScore = getDisplayPriority(currentDisplay);
  const candidateScore = getDisplayPriority(candidateDisplay);

  if (candidateScore > currentScore) return candidateDisplay;
  if (candidateScore < currentScore) return currentDisplay;
  if (candidateDisplay.length < currentDisplay.length) return candidateDisplay;
  return currentDisplay;
};

const normalizeCity = (city: string): string => city.trim().toLocaleLowerCase("ru-RU");

export const mapCsspBrands = (payload: unknown): CsspBrand[] => {
  const root = asRecord(payload);
  const candidates = asArray(root.brands ?? root.data ?? payload);

  return candidates
    .map((item, index) => {
      const brand = asRecord(item);
      const name = formatBrandForDisplay(asString(brand.name ?? brand.brand ?? brand.title ?? item));

      return {
        id: asString(brand.id ?? brand.ref_key ?? brand.Ref_Key ?? name ?? index),
        name,
        articleName:
          asString(brand.articleName ?? brand.article_name ?? brand.name_detail ?? brand.description).trim() ||
          "Деталь",
      };
    })
    .filter((brand) => brand.name.length > 0);
};

export const mapCsspSupplierResponse = (
  payload: unknown,
  params: CsspSearchParams,
  supplier: CsspSupplierCode,
  supplierIndex: number
): Pick<CsspOffersResult, "offers" | "inStock"> => {
  const payloadRoot = asRecord(payload);
  const root = asRecord(payloadRoot.data ?? payload);
  const rawOffers = asRecord(root.offers);

  const offers = CSSP_GROUP_ORDER.reduce<Record<CsspOfferGroup, CsspSupplierOffer[]>>(
    (acc, group) => {
      acc[group] = asArray(rawOffers[group])
        .map((item, index) => mapOffer(item, group, supplier, supplierIndex, index))
        .filter((offer): offer is CsspSupplierOffer => !!offer && offer.isReturn !== 0);
      return acc;
    },
    { originals: [], replacements: [], analogues: [] }
  );
  const selectedCityAliases = getSelectedCityAliases(params);

  return {
    offers,
    inStock: getRawInStockEntries(root)
      .map(({ item, group }, index) => mapInStockProduct(item, group, selectedCityAliases, supplierIndex, index))
      .filter((product): product is CsspInStockProduct => !!product && product.quantity > 0),
  };
};

export const mergeCsspResults = (
  results: Array<Pick<CsspOffersResult, "offers" | "inStock">>,
  failedSupplierCount: number
): CsspOffersResult => {
  const offers = CSSP_GROUP_ORDER.reduce<Record<CsspOfferGroup, CsspSupplierOffer[]>>(
    (acc, group) => {
      acc[group] = dedupeOffers(results.flatMap((result) => result.offers[group]));
      return acc;
    },
    { originals: [], replacements: [], analogues: [] }
  );

  return {
    offers,
    inStock: dedupeStock(results.flatMap((result) => result.inStock)),
    failedSupplierCount,
    successSupplierCount: results.length,
  };
};

const mapOffer = (
  value: unknown,
  group: CsspOfferGroup,
  supplier: CsspSupplierCode,
  supplierIndex: number,
  index: number
): CsspSupplierOffer | null => {
  const raw = asRecord(value);
  const brand = formatBrandForDisplay(asString(raw.brand ?? raw.brand_name ?? raw.maker ?? raw.manufacturer));
  const artikul = formatArticleForDisplay(
    asString(raw.artikul ?? raw.article ?? raw.sku ?? raw.number).trim()
  );
  const name = asString(raw.name ?? raw.detail_name ?? raw.title).trim();
  const description = asString(raw.description ?? raw.detail_description).trim();
  const price = asNumber(raw.price);
  const cprice = asNumber(raw.cprice);
  const quantity = asNumber(raw.stock ?? raw.quantity ?? raw.qty ?? raw.qnt ?? raw.count ?? raw.available);
  const deliveryDays = asNumber(raw.delivery_days ?? raw.dalivery_days ?? raw.deliveryDays ?? raw.delivery);
  const deliveryDaysMax = asNumber(raw.delivery_days_max ?? raw.deliveryDaysMax);
  const supplierRating = asNumber(raw.supplier_rating ?? raw.rating);
  const warehouse = asString(
    raw.warehouse ??
      raw.warehouse_name ??
      raw.warehouseName ??
      raw.stock_name ??
      raw.stockName ??
      raw.storage
  ).trim();
  const isReturnRaw = asNumber(raw.is_return);
  const isOfficialDealerRaw = asNumber(raw.is_official_dealer);
  const isPartUsedRaw = asNumber(raw.is_part_used);
  const isReturn = isReturnRaw === 0 ? 0 : isReturnRaw === 1 ? 1 : null;

  if (!brand && !artikul && !name) return null;

  return {
    id: [
      supplierIndex,
      group,
      brand,
      artikul,
      name,
      cprice ?? "no-price",
      deliveryDays ?? "no-delivery",
      quantity ?? "no-qty",
      index,
    ].join("|"),
    group,
    brand: brand || "Бренд не указан",
    artikul: artikul || "Артикул не указан",
    name: name || "Наименование не указано",
    description: description || null,
    price,
    quantity,
    deliveryDays,
    deliveryDaysMax,
    deliveryLabel: formatDelivery(deliveryDays, deliveryDaysMax, raw),
    cprice,
    // The request code is the ordering and cart identity. Backend display
    // fields must not replace it.
    supplier,
    supplierRating,
    warehouse,
    isOfficialDealer: isOfficialDealerRaw === 1 ? 1 : isOfficialDealerRaw === 0 ? 0 : null,
    isPartUsed: isPartUsedRaw === 1 ? 1 : isPartUsedRaw === 0 ? 0 : null,
    isReturn,
  };
};

const mapInStockProduct = (
  value: unknown,
  group: CsspOfferGroup,
  selectedCityAliases: string[],
  supplierIndex: number,
  index: number
): CsspInStockProduct | null => {
  const raw = asRecord(value);
  const brand = formatBrandForDisplay(asString(raw.brand ?? raw.brand_name).trim()) || "Бренд не указан";
  const artikul = formatArticleForDisplay(asString(raw.artikul ?? raw.article ?? raw.sku).trim()) || "Артикул не указан";
  const refKey =
    asString(raw.ref_key ?? raw.refKey ?? raw.Ref_Key).trim() ||
    createDerivedInStockRefKey(group, brand, artikul);
  const currentCityDirectQuantity = getCurrentCityDirectQuantity(raw);
  const cityStocks = getCityWarehouses(raw, selectedCityAliases);
  const warehouses = cityStocks.length > 0
    ? cityStocks
    : currentCityDirectQuantity > 0
      ? [{
          id: `${refKey}|current-city`,
          name: "Получение сегодня",
          quantity: currentCityDirectQuantity,
        }]
      : [];
  const cityQuantity = cityStocks.reduce((sum, stock) => sum + stock.quantity, 0);
  const totalQuantity = cityStocks.length > 0 ? cityQuantity : currentCityDirectQuantity;

  if (!refKey || totalQuantity <= 0) return null;

  return {
    id: `${refKey}|${supplierIndex}|${index}`,
    group,
    refKey,
    name: asString(raw.name ?? raw.title ?? raw.description).trim() || "Товар Scaena",
    brand,
    artikul,
    price: asNumber(raw.available && asRecord(raw.available).cost) ?? asNumber(raw.price),
    quantity: totalQuantity,
    warehouses,
  };
};

const createDerivedInStockRefKey = (
  group: CsspOfferGroup,
  brand: string,
  artikul: string
): string =>
  [
    group,
    normalizeBrandForCompare(brand),
    normalizeArticleForCompare(artikul),
  ].join("|");

const getCurrentCityDirectQuantity = (raw: RawRecord): number => {
  const available = asRecord(raw.available);

  return (
    asNumber(
      raw.current_city_quantity ??
        raw.currentCityQuantity ??
        raw.available_current_city_quantity ??
        raw.availableCurrentCityQuantity ??
        raw.city_quantity ??
        raw.cityQuantity ??
        raw.in_city_quantity ??
        raw.inCityQuantity
    ) ??
    asNumber(
      available.current_city_quantity ??
        available.currentCityQuantity ??
        available.available_current_city_quantity ??
        available.availableCurrentCityQuantity ??
        available.city_quantity ??
        available.cityQuantity
    ) ??
    0
  );
};

const getSelectedCityAliases = (params: CsspSearchParams): string[] => {
  const aliases = [params.cityName, params.cityCode];
  const city = aliases.map((value) => getCsspCity(value || "")).find(Boolean);
  if (city) aliases.push(city.code, city.name);

  return Array.from(new Set(aliases.filter(Boolean).map((value) => normalizeCity(value || ""))));
};

const getRawInStockEntries = (
  root: RawRecord
): Array<{ item: unknown; group: CsspOfferGroup }> => {
  const source = root.in_stock ?? root.inStock;
  const sourceRecord = asRecord(source);

  if (Array.isArray(source)) {
    return source
      .map((item) => {
        const group = mapInStockGroup(asRecord(item).group ?? asRecord(item).type ?? asRecord(item).relation);
        return group ? { item, group } : null;
      })
      .filter((entry): entry is { item: unknown; group: CsspOfferGroup } => !!entry);
  }

  return CSSP_GROUP_ORDER.flatMap((group) =>
    inStockGroupAliases[group].flatMap((key) =>
      asArray(sourceRecord[key]).map((item) => ({ item, group }))
    )
  );
};

const mapInStockGroup = (value: unknown): CsspOfferGroup | null => {
  const normalized = asString(value).trim().toLocaleLowerCase("ru-RU");
  if (!normalized) return null;

  return CSSP_GROUP_ORDER.find((group) => inStockGroupAliases[group].includes(normalized)) ?? null;
};

const getCityWarehouses = (raw: RawRecord, selectedCityAliases: string[]): CsspStockWarehouse[] => {
  const stockSources = getStockSources(raw);

  return stockSources
    .flatMap(({ items, cityScoped }, sourceIndex) =>
      items.map((item, index) => {
      const stock = asRecord(item);
      const name = asString(
        stock.Stock ??
          stock.stock ??
          stock.name ??
          stock.warehouse_name ??
          stock.warehouseName ??
          stock.address ??
          stock.store_name ??
          stock.storeName
      );
      const stockCity = asString(stock.city ?? stock.city_name ?? stock.cityName ?? stock.City);
      const quantity = asNumber(stock.qnt ?? stock.quantity ?? stock.qty ?? stock.count) ?? 0;
      const haystack = normalizeCity(`${stockCity} ${name}`);
      const hasCitySignal = Boolean(stockCity || name);
      const belongsToSelectedCity =
        cityScoped ||
        (hasCitySignal && selectedCityAliases.some((city) => haystack.includes(city)));

      if (!belongsToSelectedCity || quantity <= 0) return null;

      return {
        id: asString(stock.ref_key ?? stock.refKey ?? stock.Ref_Key ?? stock.id ?? name ?? `${sourceIndex}-${index}`),
        name: name || "Склад Scaena",
        quantity,
      };
    })
  )
    .filter((stock): stock is CsspStockWarehouse => !!stock);
};

const getStockSources = (
  raw: RawRecord
): Array<{ items: unknown[]; cityScoped: boolean }> => {
  const available = asRecord(raw.available);
  const sources: Array<{ items: unknown[]; cityScoped: boolean }> = [
    { items: asArrayOrSingleton(raw.availableCurrentCity), cityScoped: true },
    { items: asArrayOrSingleton(raw.available_current_city), cityScoped: true },
    { items: asArrayOrSingleton(raw.current_city_stocks), cityScoped: true },
    { items: asArrayOrSingleton(available.current_city), cityScoped: true },
    { items: asArrayOrSingleton(available.currentCity), cityScoped: true },
    { items: asArrayOrSingleton(available.availableCurrentCity), cityScoped: true },
    { items: asArray(raw.stocks), cityScoped: false },
    { items: Array.isArray(raw.stock) ? raw.stock : [], cityScoped: false },
    { items: asArray(raw.warehouses), cityScoped: false },
    { items: asArray(raw.available_by_stores), cityScoped: false },
    { items: asArray(raw.availableByStores), cityScoped: false },
    { items: asArray(available.stocks), cityScoped: false },
    { items: asArray(available.warehouses), cityScoped: false },
    { items: asArray(available.stores), cityScoped: false },
    { items: asArray(available.items), cityScoped: false },
  ];

  return sources.filter((source) => source.items.length > 0);
};

const formatDelivery = (deliveryDays: number | null, deliveryDaysMax: number | null, raw: RawRecord): string => {
  const directLabel = asString(raw.delivery_label ?? raw.deliveryLabel ?? raw.delivery_text).trim();
  if (directLabel) return directLabel;
  if (deliveryDays === null) return "Срок уточняется";
  if (deliveryDaysMax !== null && deliveryDaysMax !== deliveryDays) {
    return `${deliveryDays}-${deliveryDaysMax} дн.`;
  }
  if (deliveryDays <= 0) return "Сегодня";
  if (deliveryDays === 1) return "1 день";
  if (deliveryDays > 1 && deliveryDays < 5) return `${deliveryDays} дня`;
  return `${deliveryDays} дней`;
};

const dedupeOffers = (offers: CsspSupplierOffer[]): CsspSupplierOffer[] => {
  const seen = new Set<string>();

  return offers.filter((offer) => {
    const key = [
      offer.group,
      normalizeBrandForCompare(offer.brand),
      normalizeArticleForCompare(offer.artikul),
      offer.name.toLocaleLowerCase("ru-RU"),
      offer.supplier,
      offer.cprice ?? "",
      offer.deliveryDays ?? "",
      offer.deliveryDaysMax ?? "",
      offer.quantity ?? "",
      offer.warehouse,
    ].join("|");

    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

export const createCsspOfferGroupKey = (offer: Pick<CsspSupplierOffer, "brand" | "artikul">) =>
  `${normalizeBrandForCompare(offer.brand)}_${normalizeArticleForCompare(offer.artikul)}`;

const dedupeStock = (products: CsspInStockProduct[]): CsspInStockProduct[] => {
  const map = new Map<string, CsspInStockProduct>();

  products.forEach((product) => {
    const dedupeKey = `${product.group}|${product.refKey}`;
    const existing = map.get(dedupeKey);
    if (!existing) {
      map.set(dedupeKey, product);
      return;
    }
    const warehousesByKey = new Map<string, CsspStockWarehouse>();

    [...existing.warehouses, ...product.warehouses].forEach((warehouse) => {
      const key = normalizeCity(`${warehouse.id} ${warehouse.name}`);
      if (!warehousesByKey.has(key)) {
        warehousesByKey.set(key, warehouse);
      }
    });
    const warehouses = Array.from(warehousesByKey.values());

    map.set(dedupeKey, {
      ...existing,
      price: existing.price ?? product.price,
      quantity: warehouses.reduce((sum, warehouse) => sum + warehouse.quantity, 0),
      warehouses,
    });
  });

  return Array.from(map.values());
};
